import axios from '@/api/axios';
import {
  Fleet,
  AllFleetResponse,
  FleetResponse,
} from '@/types/api';
import apiConfig from '@/config/url-api';

import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { EditFleetFormRequest } from '@/types/form';

export const getAllFleet = async (): Promise<Fleet[]> => {
  const response = await axios.get<AllFleetResponse>(apiConfig.fleet.all);
  return response.data.data;
};

export const useQueryAllFleet = () => {
  const allFleet = useQuery({
    queryKey: [`AllFleet`],
    queryFn: ({}) => {
      return getAllFleet();
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return allFleet;
};

export const createFleet = async (params: EditFleetFormRequest): Promise<Fleet> => {
  const response = await axios.post<FleetResponse>(apiConfig.fleet.all, params);
  return response.data.data;
};

export const useMutationCreateFleet = () => {
  const queryClient = useQueryClient();

  const createNotificationMutation = useMutation({
    mutationFn: (data: { params: EditFleetFormRequest }) => createFleet(data.params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllFleet'] });
    },
  } as UseMutationOptions<Fleet, unknown, { params: EditFleetFormRequest }, unknown>);

  return createNotificationMutation;
};

export const updateFleet = async (id: string, params: EditFleetFormRequest): Promise<Fleet> => {
  const response = await axios.put<FleetResponse>(
    apiConfig.fleet.single.format({ id: id }),
    params,
  );
  return response.data.data;
};

export const useMutationUpdateFleet = () => {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; params: EditFleetFormRequest }) => updateFleet(data.id, data.params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllFleet'] });
    },
  } as UseMutationOptions<Fleet, unknown, { id: string;  params: EditFleetFormRequest }, unknown>);

  return updateMutation;
};

export const deleteFleet = async (id: string): Promise<void> => {
  await axios.delete(apiConfig.fleet.single.format({ id: id }));
};

export const useMutationDeleteFleet = () => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (data: { id: string }) => deleteFleet(data.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllFleet'] });
    },
  } as UseMutationOptions<void, unknown, { id: string }, unknown>);

  return deleteMutation;
};
